import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import type {} from '@mui/x-charts/themeAugmentation';
import type {} from '@mui/x-data-grid/themeAugmentation';
import type {} from '@mui/x-tree-view/themeAugmentation';
import { alpha } from '@mui/material/styles';
import type {} from '@mui/material/themeCssVarsAugmentation';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Header from '../dashboard/components/Header';
import ProductSelection from './components/ProductSelection';
import PaymentPage from './components/PaymentPage';
import ApiClient from '../utils/apiClient';
import AppTheme from '../shared-theme/AppTheme';
import LoadingProgress from '../register/LoadingProgress';
import { clearDateRangeCache } from '../utils/cacheUtils';
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  surfacesCustomizations,
  treeViewCustomizations,
} from '../dashboard/theme/customizations';
import withAuth from '../withAuth'

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...surfacesCustomizations,
  ...treeViewCustomizations,
};

const UpgradePage = (props: { disableCustomTheme?: boolean }) => {
  const navigate = useNavigate();
  const [selectedMenuItem, setSelectedMenuItem] = React.useState('upgrade');
  const [paymentData, setPaymentData] = React.useState<any>(null);
  const [showProgress, setShowProgress] = React.useState(false);
  const [loadedToDate, setLoadedToDate] = React.useState('');
  const [loadPercentComplete, setLoadPercentComplete] = React.useState(0);

  const handleMenuSelect = (menuItem: string) => {
    // Navigate to dashboard with the selected menu item
    navigate('/app', { state: { selectedMenuItem: menuItem } });
  };

  const handleNavigate = (page: string, data?: any) => {
    setSelectedMenuItem(page);
    if (data) {
      setPaymentData(data);
    }
  };

  const handlePaymentSuccess = React.useCallback(async () => {
    try {
      // Refresh login to get updated user data including new access level
      const data = await ApiClient.get('api/users/refresh-login');
      
      // Update localStorage with refreshed user data (same as SignIn after login)
      localStorage.setItem("token", data.token);
      localStorage.setItem("user_id", data.user.user_id);
      localStorage.setItem("access", data.access);
      
      // Clear cached date range data (earliestReading and mostRecentCompleteDay)
      // This ensures fresh data is loaded after upgrade
      clearDateRangeCache();
      
      // Show loading progress
      setShowProgress(true);
      
      // Clear existing consumption data
      await ApiClient.delete('api/connect/load/octopus');
      
      // Start loading consumption data with SSE
      const userId = localStorage.getItem("user_id");
      const eventSource = new EventSource(`${ApiClient.getCurrentBaseUrl()}/api/connect/load/octopus?id=${userId}&lookback_days=1000`);
      let timeout: NodeJS.Timeout;

      // Function to reset a timeout to close the event source connection after a period of inactivity
      const resetTimeout = () => {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          console.warn('SSE connection timeout - closing connection');
          eventSource.close();
        }, 120000); 
      };

      // Event handler for receiving messages
      eventSource.onmessage = function(event) {
        resetTimeout();
        const receivedData = JSON.parse(event.data);
        setLoadedToDate(receivedData.loaded_to_date);
        setLoadPercentComplete(receivedData.percent_complete);
        if (receivedData.load_complete) {
          clearTimeout(timeout);
          eventSource.close();
          setShowProgress(false);
          // Navigate to main dashboard
          navigate('/app', { state: { selectedMenuItem: 'account' } });
        }
      };

      // Error handler for SSE connection
      eventSource.onerror = function(event) {
        console.error('SSE connection error:', event);
        clearTimeout(timeout);
        eventSource.close();
        setShowProgress(false);
      };

      resetTimeout();
      
    } catch (error) {
      console.error('Error refreshing login after payment:', error);
      setShowProgress(false);
    }
  }, [navigate]);

  // Check for payment success in URL parameters
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    
    if (paymentStatus === 'success') {
      // Handle successful payment
      handlePaymentSuccess();
      
      // Clean up URL parameters
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    } else if (paymentStatus === 'cancelled') {
      // Handle cancelled payment
      console.log('Payment was cancelled');
      setSelectedMenuItem('upgrade');
      
      // Clean up URL parameters
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    } else if (paymentStatus === 'error') {
      // Handle payment error
      console.log('Payment error occurred');
      setSelectedMenuItem('upgrade');
      
      // Clean up URL parameters
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [handlePaymentSuccess]);

  const renderUpgradeContent = () => {
    switch (selectedMenuItem) {
      case 'upgrade':
        return <ProductSelection onNavigate={handleNavigate} />;
      case 'payment':
        return paymentData?.selectedProduct ? (
          <PaymentPage
            selectedProduct={paymentData.selectedProduct}
            onBack={() => setSelectedMenuItem('upgrade')}
            onPaymentSuccess={handlePaymentSuccess}
            onPaymentError={(error) => {
              console.error('Payment error:', error);
              setSelectedMenuItem('upgrade');
            }}
          />
        ) : (
          <div>No product selected</div>
        );
      default:
        return <ProductSelection onNavigate={handleNavigate} />;
    }
  };

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <Header onMenuSelect={handleMenuSelect} selectedMenuItem={selectedMenuItem} />
        {/* Main content */}
        <Box
          component="main"
          sx={(theme) => ({
            flexGrow: 1,
            paddingTop: { xs: 0, md: '64px' }, // Add padding to account for fixed header
            backgroundColor: theme.vars
              ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
              : alpha(theme.palette.background.default, 1),
            backgroundImage: theme.palette.mode === 'light' 
              ? 'url(/light-bg.jpg)' 
              : 'url(/dark-bg.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed',
            overflow: 'auto',
          })}
        >
          <Stack
            spacing={2}
            sx={{
              alignItems: 'center',
            }}
          >
            <Box sx={(theme) => ({ 
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              backgroundColor: 'transparent'
            })}>
              <Box sx={(theme) => ({ 
                width: '100%', 
                maxWidth: '1200px',
                backgroundColor: theme.palette.surface,
                minHeight: '100vh',
                px: { xs: 0, md: 3 },
                pb: 3
              })}>
                  {renderUpgradeContent()}
              </Box>
            </Box>
          </Stack>
        </Box>
        <LoadingProgress 
          open={showProgress} 
          loadedTo={loadedToDate} 
          progress={loadPercentComplete} 
        />
      </Box>
    </AppTheme>
  );
};

export default withAuth(UpgradePage);
